import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  AlertTriangle, 
  RefreshCw, 
  Wrench, 
  Database, 
  Users, 
  GraduationCap,
  UserCog,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DataHealthDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [credentialResults, setCredentialResults] = useState(null);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/user-credentials/full-data-health-check`, { 
        headers: getAuthHeader() 
      });
      setHealthData(res.data);
      if (res.data.status === 'healthy') {
        toast.success('All data is consistent!');
      } else {
        toast.warning(`Found ${res.data.total_issues} issues`);
      }
    } catch (error) {
      toast.error('Failed to check data health');
    } finally {
      setLoading(false);
    }
  };

  const repairAll = async () => {
    setRepairing(true);
    try {
      const res = await axios.post(`${API_URL}/api/user-credentials/repair-all-data`, {}, { 
        headers: getAuthHeader() 
      });
      toast.success('Data repair completed!');
      // Re-check health
      await checkHealth();
    } catch (error) {
      toast.error('Failed to repair data');
    } finally {
      setRepairing(false);
    }
  };

  const generateMissingCredentials = async () => {
    if (!window.confirm('This will create login credentials for ALL students without user accounts.\n\nContinue?')) {
      return;
    }
    
    setGeneratingCredentials(true);
    setCredentialResults(null);
    try {
      const res = await axios.post(`${API_URL}/api/user-credentials/bulk-create-all-missing`, {}, { 
        headers: getAuthHeader() 
      });
      setCredentialResults(res.data);
      toast.success(`Created ${res.data.created} credentials!`);
      // Re-check health
      await checkHealth();
    } catch (error) {
      toast.error('Failed to generate credentials');
    } finally {
      setGeneratingCredentials(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderIssueList = (issues, label) => {
    if (!issues || issues.length === 0) return null;
    
    return (
      <div className="mt-2 space-y-1">
        {issues.slice(0, 5).map((issue, idx) => (
          <div key={idx} className="text-xs p-2 bg-muted rounded flex justify-between items-center">
            <span>{issue.name || issue.staff_name || issue.student_id || 'Unknown'}</span>
            {issue.correct_staff_id && (
              <Badge variant="outline" className="text-xs">
                Fix: {issue.correct_staff_id.slice(0, 8)}...
              </Badge>
            )}
          </div>
        ))}
        {issues.length > 5 && (
          <p className="text-xs text-muted-foreground">...and {issues.length - 5} more</p>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="data-health-dashboard">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Data Consistency Health Check
        </CardTitle>
        <CardDescription>
          Check and repair data inconsistencies across students, staff, salary, and attendance records
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={checkHealth} 
            disabled={loading}
            data-testid="check-health-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Check Health
          </Button>
          {healthData && healthData.total_issues > 0 && (
            <Button 
              onClick={repairAll} 
              disabled={repairing}
              variant="destructive"
              data-testid="repair-all-btn"
            >
              {repairing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4 mr-2" />
              )}
              Repair All Issues
            </Button>
          )}
        </div>

        {/* Generate Missing Credentials Section */}
        {healthData && healthData.student_data?.summary?.students_without_user > 0 && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                  <Users className="h-5 w-5" />
                  <div>
                    <span className="font-medium">{healthData.student_data.summary.students_without_user} students without login credentials!</span>
                    <p className="text-sm text-amber-600">These students cannot access the student portal</p>
                  </div>
                </div>
                <Button 
                  onClick={generateMissingCredentials} 
                  disabled={generatingCredentials}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="generate-credentials-btn"
                >
                  {generatingCredentials ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UserCog className="h-4 w-4 mr-2" />
                  )}
                  Generate All Missing Credentials
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credential Generation Results */}
        {credentialResults && credentialResults.created > 0 && (
          <Card className="border-green-300 bg-green-50 dark:bg-green-950 dark:border-green-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-green-700 dark:text-green-300 text-lg flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                Credentials Generated Successfully
              </CardTitle>
              <CardDescription>
                Created {credentialResults.created} credentials, skipped {credentialResults.skipped}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-64 overflow-y-auto space-y-2">
                <p className="text-sm font-medium text-gray-600 mb-2">Login ID / Password (first 50 shown):</p>
                {credentialResults.credentials?.slice(0, 50).map((cred, idx) => (
                  <div key={idx} className="text-xs p-2 bg-white dark:bg-gray-800 rounded border flex justify-between items-center">
                    <span className="font-medium">{cred.name}</span>
                    <div className="flex gap-2">
                      <Badge variant="outline">{cred.login_id}</Badge>
                      <Badge variant="secondary">{cred.password}</Badge>
                    </div>
                  </div>
                ))}
              </div>
              {credentialResults.total_credentials > 50 && (
                <p className="text-sm text-gray-500 mt-2">
                  + {credentialResults.total_credentials - 50} more credentials created
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Health Status */}
        {healthData && (
          <div className="space-y-4">
            {/* Overall Status */}
            <div className={`p-4 rounded-lg flex items-center gap-3 ${
              healthData.status === 'healthy' 
                ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800' 
                : 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800'
            }`}>
              {healthData.status === 'healthy' ? (
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              )}
              <div>
                <p className={`font-semibold ${
                  healthData.status === 'healthy' ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'
                }`}>
                  {healthData.status === 'healthy' ? 'All Data Consistent' : `${healthData.total_issues} Issues Found`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {healthData.status === 'healthy' 
                    ? 'No data inconsistencies detected' 
                    : 'Click "Repair All Issues" to fix automatically'}
                </p>
              </div>
            </div>

            {/* Student Data Section */}
            <div className="border rounded-lg">
              <button 
                className="w-full p-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                onClick={() => toggleSection('student')}
              >
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  <span className="font-medium">Student Data</span>
                  <Badge variant={healthData.student_data.status === 'healthy' ? 'default' : 'destructive'}>
                    {healthData.student_data.issues} issues
                  </Badge>
                </div>
                {expandedSection === 'student' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {expandedSection === 'student' && (
                <div className="p-3 border-t space-y-2">
                  {Object.entries(healthData.student_data.summary).map(([key, count]) => (
                    <div key={key} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                      <Badge variant={count > 0 ? 'destructive' : 'secondary'}>{count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Staff Data Section */}
            <div className="border rounded-lg">
              <button 
                className="w-full p-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                onClick={() => toggleSection('staff')}
              >
                <div className="flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  <span className="font-medium">Staff & Salary Data</span>
                  <Badge variant={healthData.staff_data.status === 'healthy' ? 'default' : 'destructive'}>
                    {healthData.staff_data.issues} issues
                  </Badge>
                </div>
                {expandedSection === 'staff' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {expandedSection === 'staff' && (
                <div className="p-3 border-t space-y-2">
                  {/* Stats */}
                  {healthData.staff_data.stats && (
                    <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b">
                      <div className="text-center">
                        <p className="text-2xl font-bold">{healthData.staff_data.stats.total_teachers}</p>
                        <p className="text-xs text-muted-foreground">Teachers</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold">{healthData.staff_data.stats.total_staff}</p>
                        <p className="text-xs text-muted-foreground">Staff</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold">{healthData.staff_data.stats.total_salary_structures}</p>
                        <p className="text-xs text-muted-foreground">Salary Structures</p>
                      </div>
                    </div>
                  )}
                  {/* Issues */}
                  {Object.entries(healthData.staff_data.summary).map(([key, count]) => (
                    <div key={key} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                      <Badge variant={count > 0 ? 'destructive' : 'secondary'}>{count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        {!healthData && (
          <div className="text-center py-6 text-muted-foreground">
            <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Click "Check Health" to scan for data inconsistencies</p>
            <p className="text-sm mt-1">This will check students, staff, salary structures, and attendance records</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataHealthDashboard;
